import * as THREE from 'three';
import { basicMaterial, type MaterialProvider } from './materials';

/** Правило каталога материалов: id (по regex) → карты/параметры PBR. */
interface MaterialRule {
  match: string; // регэксп по materialId (регистронезависимо)
  baseColor?: string; // путь к карте базового цвета (sRGB)
  normal?: string; // путь к карте нормалей (linear)
  color?: string; // тонирующий цвет (умножается на карту), hex
  metalness?: number;
  roughness?: number;
  normalScale?: number; // сила рельефа нормали (для рельефных волокон)
  envMapIntensity?: number; // вклад IBL (меньше — фактура не вымывается)
  tiling?: [number, number]; // повтор текстуры [u, v] (повторов на метр для полотна)
}

interface MaterialCatalog {
  rules?: MaterialRule[];
}

interface Compiled {
  re: RegExp;
  mat: THREE.MeshStandardMaterial;
}

/**
 * Загрузить каталог материалов и текстуры → провайдер материалов с реальными
 * PBR-картами (алюминий, васи). Материалы — данные (ADR content-pipeline):
 * добавление/замена без пересборки. При отсутствии совпадения — базовый цвет.
 */
export async function loadMaterialLibrary(baseUrl = 'content/'): Promise<MaterialProvider> {
  const catalog = (await fetch(`${baseUrl}catalog/materials.json`).then((r) => {
    if (!r.ok) throw new Error(`materials HTTP ${r.status}`);
    return r.json();
  })) as MaterialCatalog;

  const loader = new THREE.TextureLoader();
  const loadTex = async (
    url: string,
    srgb: boolean,
    tiling: [number, number],
  ): Promise<THREE.Texture> => {
    const tex = await loader.loadAsync(`${baseUrl}${url}`);
    tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    // Как в старом 3D (BaseMaterial.cs): простой Repeat; тайлинг = физ.размер ×
    // (tiling — повторов на метр). Текстура бесшовная, узор не читается.
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(tiling[0], tiling[1]);
    // Анизотропная фильтрация: мелкая (много повторов) фактура не усредняется в
    // серый при минификации/под углом (как в Unity по умолчанию).
    tex.anisotropy = 16;
    tex.needsUpdate = true;
    return tex;
  };

  const rules = catalog.rules ?? [];
  const compiled: Compiled[] = await Promise.all(
    rules.map(async (r) => {
      const tiling = r.tiling ?? [1, 1];
      const mat = new THREE.MeshStandardMaterial({
        color: r.color ?? 0xffffff,
        metalness: r.metalness ?? 0.0,
        roughness: r.roughness ?? 0.85,
      });
      // Меньше вклад IBL (чтобы фактура полотна не вымывалась ярким окружением).
      if (r.envMapIntensity != null) mat.envMapIntensity = r.envMapIntensity;
      if (r.baseColor) mat.map = await loadTex(r.baseColor, true, tiling);
      if (r.normal) {
        mat.normalMap = await loadTex(r.normal, false, tiling);
        if (r.normalScale) mat.normalScale.set(r.normalScale, r.normalScale);
      }
      return { re: new RegExp(r.match, 'i'), mat };
    }),
  );

  const cache = new Map<string, THREE.Material>();
  return (materialId) => {
    const key = materialId ?? '__default__';
    const cached = cache.get(key);
    if (cached) return cached;
    const hit = materialId ? compiled.find((c) => c.re.test(materialId)) : undefined;
    const mat = hit ? hit.mat : basicMaterial(materialId);
    cache.set(key, mat);
    return mat;
  };
}
